import React, { useState } from 'react'
import { Popover } from 'antd'
import AiDraftWorkflowOrchestrator from './WorkflowOrchestrator'
import AiLegacyDraftWorkflowOrchestrator from './LegacyWorkflowOrchestrator'
import AiDraftErrorBoundary from './ErrorBoundary'
import type { AiDraftPopoverProps } from './types'
import { WorkflowState } from '../../../models/aiAnalysis/workflow'
import { buildActions, hasCombinedActions, resolveLegacyPair } from '../../../utils/aiAnalysis/draftPayload'
import styles from './styles.module.scss'

const POPOVER_WIDTH = '333px'

const suppressionKey = (actionId: string) => `ai-draft-suppressed-${actionId}`

const AiDraftPopover: React.FC<AiDraftPopoverProps> = ({
  forwardAction,
  acknowledgeAction,
  combinedToClientAction,
  combinedToNotaryAction,
  autoOpen,
  placement = 'topRight',
  maxWidth = '90vw',
  displayTimeZone
}) => {
  const actions = buildActions({
    forwardAction,
    acknowledgeAction,
    combinedToClientAction,
    combinedToNotaryAction
  })

  // The per-recipient card view is gated to combined payloads. A legacy
  // payload — a pending forward+acknowledge pair, or a single pending
  // forward or acknowledge — uses the sequential flow (forward editor ->
  // sent interstitial -> acknowledge editor; an ack-only payload opens
  // directly in the acknowledge step).
  const hasCombined = hasCombinedActions(actions)

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (!autoOpen) return false
    // Suppress auto-open only when EVERY currently-rendered action id is already
    // suppressed. Suppression is keyed per action id (not once per popover): a
    // fatal dismiss closes the whole popover, but on the next load the backend
    // may return a different pending set — if a sibling that was never suppressed
    // is now the sole pending action, it must still auto-open. Suppression
    // persists for the session (not cleared on read) so a still-erroring action
    // doesn't reopen on every reload; it lifts when the tab session ends or the
    // action is decided (decided actions aren't passed back in as candidates).
    if (actions.length === 0) return false
    const allSuppressed = actions.every((action) => !!sessionStorage.getItem(suppressionKey(action.id)))
    return !allSuppressed
  })
  const [currentWorkflowState, setCurrentWorkflowState] = useState<WorkflowState | null>(null)

  const legacyPair = resolveLegacyPair({ forwardAction, acknowledgeAction })

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleFatalDismiss = () => {
    // Suppress each currently-pending action individually. The popover closes
    // wholesale on a fatal dismiss, so mark every action rendered in it; a later
    // load re-opens only if it surfaces an action id that was never marked here
    // (see the isOpen initializer's all-suppressed predicate).
    actions.forEach((action) => sessionStorage.setItem(suppressionKey(action.id), '1'))
    handleClose()
    window.location.reload()
  }

  const handleErrorBoundaryClose = () => {
    // Closing leaves any pending actions undecided — only the explicit Decline
    // button in a card decides an action. Pending actions expire on their
    // own staleness schedule if nobody acts on them.
    handleClose()
  }

  const handleWorkflowStateChange = (state: WorkflowState) => {
    setCurrentWorkflowState(state)
  }

  // The reply-style visual treatment (white background instead of the default
  // popover background) applies whenever the popover has no forward card —
  // i.e. every remaining card is a reply to something already sent (legacy
  // acknowledge-only) or a post-combine terminal response. For the legacy
  // sequential flow it also applies once the flow has advanced into the
  // acknowledge step.
  const isSecondMessage = hasCombined
    ? !forwardAction && actions.length > 0
    : currentWorkflowState === WorkflowState.ACKNOWLEDGE_DRAFT || (!forwardAction && acknowledgeAction != null)

  if (actions.length === 0) {
    return null
  }

  return (
    <Popover
      content={
        <AiDraftErrorBoundary onClose={handleErrorBoundaryClose}>
          {hasCombined ? (
            <AiDraftWorkflowOrchestrator
              actions={actions}
              isOpen={isOpen}
              onClose={handleClose}
              onFatalDismiss={handleFatalDismiss}
              displayTimeZone={displayTimeZone}
            />
          ) : (
            <AiLegacyDraftWorkflowOrchestrator
              forwardAction={legacyPair.forwardAction}
              acknowledgeAction={legacyPair.acknowledgeAction}
              isOpen={isOpen}
              onClose={handleClose}
              onFatalDismiss={handleFatalDismiss}
              onWorkflowStateChange={handleWorkflowStateChange}
            />
          )}
        </AiDraftErrorBoundary>
      }
      placement={placement}
      open={isOpen}
      trigger={[]}
      overlayClassName={`ai-draft-popover-overlay ${isSecondMessage ? 'ai-draft-popover-second-message' : ''}`}
      overlayInnerStyle={{
        width: POPOVER_WIDTH,
        maxWidth: maxWidth
      }}
      align={{
        overflow: { adjustX: true, adjustY: false }
      }}
      getPopupContainer={() => document.body}
    >
      <div className={`${styles.anchor} ai-draft-popover-anchor`} data-latest-draft={autoOpen ? 'true' : undefined} />
    </Popover>
  )
}

export default AiDraftPopover
